import { DEFAULT_SHOP_LOCALE, INTERNAL_BYPASS_PATHS, type LocaleResolutionOptions, type ShopLocale } from './shared'
import { localeToPathSegment, normalizeActiveShopLocales, normalizeShopLocale } from './normalization'

const isExternalLikePath = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('#')) return true
  if (trimmed.startsWith('//')) return true
  return /^[a-z][a-z\d+\-.]*:/i.test(trimmed)
}

const isInternalBypassPath = (path: string) =>
  path === '/admin' ||
  path.startsWith('/admin/') ||
  path === '/login' ||
  path.startsWith('/login/') ||
  path === '/signup' ||
  path.startsWith('/signup/') ||
  path === '/dashboard' ||
  path.startsWith('/dashboard/') ||
  path === '/api' ||
  path.startsWith('/api/')

const normalizePathname = (value: string) => {
  const normalized = `/${String(value || '').replace(/^\/+|\/+$/g, '')}`
  return normalized === '/' ? normalized : normalized.replace(/\/{2,}/g, '/')
}

const splitPathAndSuffix = (value: string) => {
  const input = String(value || '')
  const queryIndex = input.indexOf('?')
  const hashIndex = input.indexOf('#')
  const splitIndex =
    queryIndex === -1
      ? hashIndex
      : hashIndex === -1
      ? queryIndex
      : Math.min(queryIndex, hashIndex)

  if (splitIndex === -1) {
    return { path: input, suffix: '' }
  }

  return {
    path: input.slice(0, splitIndex),
    suffix: input.slice(splitIndex),
  }
}

const resolveLocalePathSegment = (locale: ShopLocale, options?: LocaleResolutionOptions) => {
  const availableLocales = normalizeActiveShopLocales(options?.availableLocales)
  const localeOptions = { availableLocales }
  const resolvedLocale =
    normalizeShopLocale(locale, localeOptions) ??
    normalizeShopLocale(DEFAULT_SHOP_LOCALE, localeOptions) ??
    availableLocales[0] ??
    DEFAULT_SHOP_LOCALE

  return localeToPathSegment(resolvedLocale)
}

export const stripLocalePrefix = (
  pathname: string,
  options?: LocaleResolutionOptions,
): {
  localeFromPath: ShopLocale | null
  hadLocalePrefix: boolean
  pathnameWithoutLocale: string
} => {
  const normalized = normalizePathname(pathname)
  const segments = normalized.split('/').filter(Boolean)
  const head = segments[0]?.toLowerCase() || ''

  if (INTERNAL_BYPASS_PATHS.has(head)) {
    return {
      localeFromPath: null,
      hadLocalePrefix: false,
      pathnameWithoutLocale: normalized,
    }
  }

  const availablePathSegments = new Set(
    normalizeActiveShopLocales(options?.availableLocales).map((locale) => localeToPathSegment(locale)),
  )
  const localeFromPath = availablePathSegments.has(head) ? normalizeShopLocale(head, options) : null
  if (localeFromPath && localeToPathSegment(localeFromPath) === head) {
    const rest = segments.slice(1)
    return {
      localeFromPath,
      hadLocalePrefix: true,
      pathnameWithoutLocale: rest.length ? `/${rest.join('/')}` : '/',
    }
  }

  return {
    localeFromPath: null,
    hadLocalePrefix: false,
    pathnameWithoutLocale: normalized,
  }
}

export const inferLocaleFromPathname = (
  pathname: string,
  options?: LocaleResolutionOptions,
): ShopLocale | null => stripLocalePrefix(pathname, options).localeFromPath

export const withLocalePrefix = (
  targetPath: string,
  locale: ShopLocale,
  options?: LocaleResolutionOptions,
): string => {
  const input = String(targetPath || '').trim()
  const segment = resolveLocalePathSegment(locale, options)
  if (!input) {
    return `/${segment}`
  }

  if (isExternalLikePath(input)) {
    return input
  }

  const { path, suffix } = splitPathAndSuffix(input)
  const normalizedPath = normalizePathname(path || '/')
  if (isInternalBypassPath(normalizedPath)) {
    return `${normalizedPath}${suffix}`
  }
  const { pathnameWithoutLocale } = stripLocalePrefix(normalizedPath, options)
  const prefix = `/${segment}`
  const localizedPath = pathnameWithoutLocale === '/' ? prefix : `${prefix}${pathnameWithoutLocale}`
  return `${localizedPath}${suffix}`
}
